import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Typography } from 'antd';
import { TrophyOutlined, ReloadOutlined, UpOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const MaveshiJump = ({ onGameComplete }) => {
    const [gameStarted, setGameStarted] = useState(false);
    const [gameEnded, setGameEnded] = useState(false);
    const [showStartScreen, setShowStartScreen] = useState(true);
    const [showCharacterSelect, setShowCharacterSelect] = useState(false);
    const [showPressToStart, setShowPressToStart] = useState(false);
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(0);
    const [playerY, setPlayerY] = useState(250); // Player vertical position (center of screen)
    const [velocityY, setVelocityY] = useState(0); // Vertical velocity
    const [obstacles, setObstacles] = useState([]);
    const [collectibles, setCollectibles] = useState([]);
    const [gameSpeed, setGameSpeed] = useState(2);
    const [targetGameSpeed, setTargetGameSpeed] = useState(2);
    const [collisionAnimation, setCollisionAnimation] = useState(false);
    const [explosionParticles, setExplosionParticles] = useState([]);
    const [collectedCount, setCollectedCount] = useState(0);
    
    const gameAreaRef = useRef(null);
    const animationFrameRef = useRef(null);
    const playerEmojiRef = useRef('🐄');
    const velocityYRef = useRef(0);
    const playerYRef = useRef(250);
    const scoreRef = useRef(0);
    const obstacleIdRef = useRef(0);
    const lastObstacleXRef = useRef(400);
    const collectibleIdRef = useRef(0);
    
    // Audio refs
    const snapAudioRef = useRef(null);
    const explosionAudioRef = useRef(null);
    const collectAudioRef = useRef(null);
    const failAudioRef = useRef(null);
    
    // Different livestock characters
    const livestockCharacters = [
        { emoji: '🐄', name: 'Cow' },
        { emoji: '🐂', name: 'Ox' },
        { emoji: '🐃', name: 'Water Buffalo' },
        { emoji: '🐑', name: 'Sheep' },
        { emoji: '🐐', name: 'Goat' },
        { emoji: '🐎', name: 'Horse' },
        { emoji: '🐴', name: 'Horse Face' },
        { emoji: '🫏', name: 'Donkey' },
        { emoji: '🐔', name: 'Chicken' },
        { emoji: '🐓', name: 'Rooster' },
        { emoji: '🦆', name: 'Duck' },
        { emoji: '🦃', name: 'Turkey' }
    ];

    // Initialize with random character if none selected
    useEffect(() => {
        if (!selectedCharacter) {
            const randomChar = livestockCharacters[Math.floor(Math.random() * livestockCharacters.length)];
            setSelectedCharacter(randomChar);
            playerEmojiRef.current = randomChar.emoji;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize audio files
    useEffect(() => {
        snapAudioRef.current = new Audio('/sounds/snap.mp3');
        snapAudioRef.current.volume = 0.6; // Set volume to 60%
        
        explosionAudioRef.current = new Audio('/sounds/explosion.mp3');
        explosionAudioRef.current.volume = 0.7; // Set volume to 70%
        
        collectAudioRef.current = new Audio('/sounds/collect.mp3');
        collectAudioRef.current.volume = 0.6; // Set volume to 60%
        
        failAudioRef.current = new Audio('/sounds/fail.mp3');
        failAudioRef.current.volume = 0.7; // Set volume to 70%
        
        return () => {
            if (snapAudioRef.current) {
                snapAudioRef.current.pause();
                snapAudioRef.current = null;
            }
            if (explosionAudioRef.current) {
                explosionAudioRef.current.pause();
                explosionAudioRef.current = null;
            }
            if (collectAudioRef.current) {
                collectAudioRef.current.pause();
                collectAudioRef.current = null;
            }
            if (failAudioRef.current) {
                failAudioRef.current.pause();
                failAudioRef.current = null;
            }
        };
    }, []);

    // Load high score from localStorage
    useEffect(() => {
        const savedHighScore = localStorage.getItem('maveshiJumpHighScore');
        if (savedHighScore) {
            setHighScore(parseInt(savedHighScore, 10));
        }
    }, []);

    // Sync refs with state
    useEffect(() => {
        velocityYRef.current = velocityY;
        playerYRef.current = playerY;
        scoreRef.current = score;
    }, [velocityY, playerY, score]);

    // Handle jump (space, click, or touch)
    const handleJump = useCallback(() => {
        if (!gameStarted || gameEnded || collisionAnimation) return;
        
        // Play snap sound when jumping
        if (snapAudioRef.current) {
            try {
                snapAudioRef.current.currentTime = 0; // Reset to start
                snapAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play snap sound:', error);
                });
            } catch (error) {
                console.log('Error playing snap sound:', error);
            }
        }
        
        // Smoother jump with slightly less force for better control
        velocityYRef.current = -7.5; // Jump force (negative = up)
        setVelocityY(velocityYRef.current);
    }, [gameStarted, gameEnded, collisionAnimation]);

    // Handle press to start
    const handlePressToStart = useCallback(() => {
        if (showPressToStart && !gameStarted) {
            setShowPressToStart(false);
            setGameStarted(true);
        }
    }, [showPressToStart, gameStarted]);

    // Keyboard and mouse controls
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
                e.preventDefault();
                if (showPressToStart && !gameStarted) {
                    handlePressToStart();
                } else if (gameStarted && !gameEnded && !collisionAnimation) {
                    handleJump();
                }
            }
        };

        const handleClick = (e) => {
            // Only handle clicks on the game area
            if (showPressToStart && !gameStarted) {
                handlePressToStart();
            } else if (gameStarted && !gameEnded && !collisionAnimation) {
                handleJump();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        const gameArea = gameAreaRef.current;
        if (gameArea) {
            gameArea.addEventListener('click', handleClick);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            if (gameArea) {
                gameArea.removeEventListener('click', handleClick);
            }
        };
    }, [handleJump, handlePressToStart, showPressToStart, gameStarted, gameEnded, collisionAnimation]);

    // Animate explosion particles
    useEffect(() => {
        if (explosionParticles.length === 0) return;

        const particleInterval = setInterval(() => {
            setExplosionParticles(prev => {
                return prev.map(particle => ({
                    ...particle,
                    x: particle.x + particle.vx * 0.3, // Slower horizontal movement
                    y: particle.y + particle.vy * 0.3, // Slower vertical movement
                    vy: particle.vy + 0.15, // Gravity for smoke (slower)
                    size: particle.size + 0.8, // Gradual size increase
                    opacity: Math.max(0, particle.opacity - 0.015),
                    life: particle.life - 0.015
                })).filter(particle => particle.life > 0 && particle.opacity > 0);
            });
        }, 16); // ~60fps

        return () => clearInterval(particleInterval);
    }, [explosionParticles.length]);

    // Game loop
    useEffect(() => {
        if (!gameStarted || gameEnded || collisionAnimation) {
            // Cancel any running animation frame
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            return;
        }

        const gameLoop = () => {
            // Smooth speed transition
            if (Math.abs(gameSpeed - targetGameSpeed) > 0.01) {
                setGameSpeed(prev => {
                    const diff = targetGameSpeed - prev;
                    return prev + diff * 0.1; // Smooth interpolation
                });
            }

            // Apply gravity (smoother)
            velocityYRef.current += 0.35; // Reduced gravity for more normal falling speed
            setVelocityY(velocityYRef.current);

            // Update player Y position with smoother interpolation
            let newY = playerYRef.current + velocityYRef.current;
            
            // Keep player within screen bounds (with some margin)
            if (newY < 30) {
                newY = 30;
                velocityYRef.current = 0;
                setVelocityY(0);
            }
            if (newY > 470) {
                // Hit the ground - play fail sound and game over
                if (failAudioRef.current) {
                    try {
                        failAudioRef.current.currentTime = 0; // Reset to start
                        failAudioRef.current.play().catch(error => {
                            console.log('Could not play fail sound:', error);
                        });
                    } catch (error) {
                        console.log('Error playing fail sound:', error);
                    }
                }
                
                setGameEnded(true);
                setGameStarted(false);
                
                if (scoreRef.current > highScore) {
                    setHighScore(scoreRef.current);
                    localStorage.setItem('maveshiJumpHighScore', scoreRef.current.toString());
                }
                
                if (onGameComplete) {
                    setTimeout(() => onGameComplete(scoreRef.current), 500);
                }
                return;
            }

            playerYRef.current = newY;
            setPlayerY(newY);

            // Move collectibles and check collection
            setCollectibles(prevCollectibles => {
                return prevCollectibles.map(collectible => {
                    if (collectible.collected) return collectible;
                    
                    const collectibleX = collectible.x - gameSpeed;
                    
                    // Check collision with player using pixel coordinates
                    // Player position in pixels (left side of screen)
                    const playerLeft = 50; // Player X position in pixels
                    const playerRight = 50 + 25; // Player width in pixels (larger hitbox)
                    const playerTop = playerYRef.current - 25; // Player top in pixels
                    const playerBottom = playerYRef.current + 25; // Player bottom in pixels
                    
                    // Collectible position in pixels
                    const collectibleLeft = collectibleX - 20; // Collectible left edge
                    const collectibleRight = collectibleX + 20; // Collectible right edge
                    const collectibleTop = collectible.y - 20; // Collectible top edge
                    const collectibleBottom = collectible.y + 20; // Collectible bottom edge
                    
                    // Character collision detection - check if character overlaps with collectible
                    if (playerRight >= collectibleLeft &&
                        playerLeft <= collectibleRight &&
                        playerBottom >= collectibleTop &&
                        playerTop <= collectibleBottom) {
                        // Collected by character collision! Play collect sound
                        if (collectAudioRef.current) {
                            try {
                                collectAudioRef.current.currentTime = 0; // Reset to start
                                collectAudioRef.current.play().catch(error => {
                                    console.log('Could not play collect sound:', error);
                                });
                            } catch (error) {
                                console.log('Error playing collect sound:', error);
                            }
                        }
                        
                        scoreRef.current += 5; // Bonus points for collecting
                        setScore(scoreRef.current);
                        setCollectedCount(prev => prev + 1);
                        return {
                            ...collectible,
                            collected: true,
                            x: collectibleX
                        };
                    }
                    
                    return {
                        ...collectible,
                        x: collectibleX
                    };
                }).filter(collectible => !collectible.collected && collectible.x > -100);
            });

            // Move obstacles and check collisions
            setObstacles(prev => {
                const updated = prev.map(obstacle => {
                    const newX = obstacle.x - gameSpeed;
                    
                    // Check collision with player
                    const playerLeft = 50; // Player X position (center of screen)
                    const playerRight = 50 + 8; // Player width
                    const playerTop = playerYRef.current - 20;
                    const playerBottom = playerYRef.current + 20;
                    
                    // Obstacle collision detection
                    if (newX < playerRight && newX + obstacle.width > playerLeft) {
                        // Check if player hits top or bottom obstacle
                        const hitTop = playerTop < obstacle.topHeight;
                        const hitBottom = playerBottom > 500 - obstacle.bottomHeight;
                        
                        if (hitTop || hitBottom) {
                            // Calculate exact collision point at the obstacle
                            // Obstacle x is in pixels, convert to percentage for rendering
                            const gameAreaWidth = gameAreaRef.current?.offsetWidth || 400;
                            const obstacleCenterX = newX + (obstacle.width / 2);
                            const collisionXPercent = (obstacleCenterX / gameAreaWidth) * 100;
                            
                            // Calculate collision Y position at the obstacle edge
                            let collisionY;
                            if (hitTop) {
                                // Hit top obstacle - collision at the bottom edge of top obstacle
                                collisionY = obstacle.topHeight; // Bottom edge of top obstacle
                            } else {
                                // Hit bottom obstacle - collision at the top edge of bottom obstacle
                                collisionY = 500 - obstacle.bottomHeight; // Top edge of bottom obstacle
                            }
                            
                            // Create smoke particles at collision point
                            const particles = [];
                            for (let i = 0; i < 25; i++) {
                                const angle = (Math.PI * 2 * i) / 25;
                                const speed = 2 + Math.random() * 4;
                                particles.push({
                                    id: Date.now() + i,
                                    x: collisionXPercent,
                                    y: collisionY,
                                    vx: Math.cos(angle) * speed,
                                    vy: Math.sin(angle) * speed - 1.5, // Upward bias for smoke
                                    size: 20 + Math.random() * 25,
                                    opacity: 0.9,
                                    life: 1.0
                                });
                            }
                            
                            setExplosionParticles(particles);
                            setCollisionAnimation(true);
                            
                            // Play explosion sound on collision
                            if (explosionAudioRef.current) {
                                try {
                                    explosionAudioRef.current.currentTime = 0; // Reset to start
                                    explosionAudioRef.current.play().catch(error => {
                                        console.log('Could not play explosion sound:', error);
                                    });
                                } catch (error) {
                                    console.log('Error playing explosion sound:', error);
                                }
                            }
                            
                            // Hide player immediately
                            playerEmojiRef.current = '';
                            
                            // Game over after explosion animation
                            setTimeout(() => {
                                setGameEnded(true);
                                setGameStarted(false);
                                
                                if (scoreRef.current > highScore) {
                                    setHighScore(scoreRef.current);
                                    localStorage.setItem('maveshiJumpHighScore', scoreRef.current.toString());
                                }
                                
                                if (onGameComplete) {
                                    setTimeout(() => onGameComplete(scoreRef.current), 500);
                                }
                            }, 800); // Delay for explosion animation
                        }
                    }
                    
                    // Update score when passing obstacle
                    if (!obstacle.passed && newX + obstacle.width < playerLeft) {
                        obstacle.passed = true;
                        scoreRef.current += 1;
                        setScore(scoreRef.current);
                        
                        // Smoothly increase game speed (gradual increase)
                        setTargetGameSpeed(prev => Math.min(prev + 0.05, 4.5));
                    }
                    
                    return {
                        ...obstacle,
                        x: newX
                    };
                }).filter(obstacle => obstacle.x > -100); // Remove obstacles that are off screen
                
                // Generate new obstacles
                if (updated.length === 0 || lastObstacleXRef.current - updated[updated.length - 1].x > 250) {
                    const gapSize = 120 + Math.random() * 60; // Gap between top and bottom obstacles
                    const gapPosition = 100 + Math.random() * (300 - gapSize); // Random gap position
                    const gapCenter = gapPosition + (gapSize / 2);
                    
                    updated.push({
                        id: obstacleIdRef.current++,
                        x: 400,
                        width: 60,
                        topHeight: gapPosition,
                        bottomHeight: 500 - gapPosition - gapSize,
                        passed: false
                    });
                    
                    // Create collectible in the gap
                    setCollectibles(prev => {
                        const gameAreaWidth = gameAreaRef.current?.offsetWidth || 400;
                        return [...prev, {
                            id: collectibleIdRef.current++,
                            x: 400 + 30, // Center of obstacle
                            y: gapCenter, // Center of gap
                            collected: false,
                            type: Math.random() > 0.7 ? 'star' : 'coin' // 30% chance for star
                        }];
                    });
                    
                    lastObstacleXRef.current = 400;
                }
                
                return updated;
            });

            animationFrameRef.current = requestAnimationFrame(gameLoop);
        };

        animationFrameRef.current = requestAnimationFrame(gameLoop);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [gameStarted, gameEnded, collisionAnimation, targetGameSpeed, highScore, onGameComplete]);

    const startGame = () => {
        if (!selectedCharacter) {
            setShowStartScreen(false);
            setShowCharacterSelect(true);
            return;
        }
        
        // Cancel any running animation frames first
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        
        // Reset game state
        setGameEnded(false);
        setGameStarted(false); // Reset gameStarted first
        setShowStartScreen(false);
        setShowCharacterSelect(false);
        setShowPressToStart(true);
        setScore(0);
        setPlayerY(250);
        setVelocityY(0);
        setObstacles([]);
        setCollectibles([]);
        setGameSpeed(2);
        setTargetGameSpeed(2);
        setCollisionAnimation(false);
        setCollectedCount(0);
        setExplosionParticles([]);
        
        // Reset refs
        velocityYRef.current = 0;
        playerYRef.current = 250;
        scoreRef.current = 0;
        obstacleIdRef.current = 0;
        collectibleIdRef.current = 0;
        lastObstacleXRef.current = 400;
        
        // Set selected character (restore if it was cleared)
        if (!playerEmojiRef.current && selectedCharacter) {
            playerEmojiRef.current = selectedCharacter.emoji;
        } else if (selectedCharacter) {
            playerEmojiRef.current = selectedCharacter.emoji;
        }
        
        // Create first obstacle
        const gapSize = 120 + Math.random() * 60;
        const gapPosition = 100 + Math.random() * (300 - gapSize);
        const gapCenter = gapPosition + (gapSize / 2);
        
        setObstacles([{
            id: obstacleIdRef.current++,
            x: 400,
            width: 60,
            topHeight: gapPosition,
            bottomHeight: 500 - gapPosition - gapSize,
            passed: false
        }]);
        
        // Create first collectible
        const gameAreaWidth = gameAreaRef.current?.offsetWidth || 400;
        setCollectibles([{
            id: collectibleIdRef.current++,
            x: 400 + 30, // Center of obstacle
            y: gapCenter, // Center of gap
            collected: false,
            type: 'coin'
        }]);
    };

    const selectCharacter = (character) => {
        setSelectedCharacter(character);
        playerEmojiRef.current = character.emoji;
    };

    const getScoreMessage = () => {
        if (score >= 100) return "🏆 Legendary! You're a flying master!";
        if (score >= 50) return "⭐ Amazing! Incredible skills!";
        if (score >= 30) return "👍 Great! Keep flying!";
        if (score >= 15) return "📚 Good! Practice makes perfect!";
        return "💪 Keep practicing!";
    };

    // First screen - Image with Start button
    if (showStartScreen && !showCharacterSelect && !gameStarted && !gameEnded) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '0',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                overflow: 'hidden'
            }}>
                {/* Game Preview Image */}
                <div style={{
                    width: '100%',
                    height: '400px',
                    background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 30%, #98D8C8 70%, #90EE90 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img 
                        src="/assets/images/maveshi_jump.jpg" 
                        alt="Maveshi Jump Game"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center'
                        }}
                        onError={(e) => {
                            // Fallback if image doesn't exist
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
                
                {/* Start Button Section */}
                <div style={{
                    padding: '20px 16px',
                    background: 'rgba(255, 255, 255, 0.95)'
                }}>
                    <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '16px' }}>
                        🦘 Maveshi Jump
                    </Title>
                    <Text style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px'
                    }}>
                        Fly through obstacles and collect coins!
                    </Text>
                    {highScore > 0 && (
                        <Text style={{ 
                            fontSize: '14px', 
                            color: '#fa8c16',
                            display: 'block',
                            marginBottom: '15px',
                            fontWeight: 600
                        }}>
                            🏆 High Score: {highScore}
                        </Text>
                    )}
                    <Button
                        type="primary"
                        size="middle"
                        icon={<TrophyOutlined />}
                        onClick={() => {
                            setShowStartScreen(false);
                            setShowCharacterSelect(true);
                        }}
                        style={{
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '0 20px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Select Character
                    </Button>
                </div>
            </div>
        );
    }

    // Character selection screen (second screen)
    if (showCharacterSelect && !gameStarted && !gameEnded) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '20px 16px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <Text style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    display: 'block',
                    marginBottom: '20px'
                }}>
                    Select Your Character
                </Text>
                
                {/* Character Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    marginBottom: '20px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: '10px'
                }}>
                    {livestockCharacters.map((character, index) => (
                        <div
                            key={index}
                            onClick={() => selectCharacter(character)}
                            style={{
                                padding: '12px',
                                borderRadius: '10px',
                                border: selectedCharacter?.emoji === character.emoji 
                                    ? '3px solid #1890ff' 
                                    : '2px solid #d9d9d9',
                                background: selectedCharacter?.emoji === character.emoji
                                    ? 'linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(24, 144, 255, 0.05) 100%)'
                                    : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                transform: selectedCharacter?.emoji === character.emoji ? 'scale(1.05)' : 'scale(1)',
                                boxShadow: selectedCharacter?.emoji === character.emoji
                                    ? '0 4px 12px rgba(24, 144, 255, 0.3)'
                                    : '0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                                if (selectedCharacter?.emoji !== character.emoji) {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedCharacter?.emoji !== character.emoji) {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                }
                            }}
                        >
                            <div style={{ fontSize: '48px', marginBottom: '8px', transform: 'scaleX(-1)' }}>
                                {character.emoji}
                            </div>
                            <Text style={{ 
                                fontSize: '12px', 
                                color: selectedCharacter?.emoji === character.emoji ? '#1890ff' : '#666',
                                fontWeight: selectedCharacter?.emoji === character.emoji ? 600 : 400
                            }}>
                                {character.name}
                            </Text>
                        </div>
                    ))}
                </div>

                {highScore > 0 && (
                    <Text style={{ 
                        fontSize: '14px', 
                        color: '#fa8c16',
                        display: 'block',
                        marginBottom: '15px',
                        fontWeight: 600
                    }}>
                        🏆 High Score: {highScore}
                    </Text>
                )}

                <Button
                    type="primary"
                    size="large"
                    icon={<TrophyOutlined />}
                    onClick={startGame}
                    disabled={!selectedCharacter}
                    style={{
                        height: '44px',
                        fontSize: '15px',
                        fontWeight: 600,
                        borderRadius: '10px',
                        padding: '0 24px',
                        background: selectedCharacter
                            ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)'
                            : 'linear-gradient(135deg, #d9d9d9 0%, #bfbfbf 100%)',
                        border: 'none',
                        boxShadow: selectedCharacter
                            ? '0 4px 12px rgba(24, 144, 255, 0.3)'
                            : 'none',
                        cursor: selectedCharacter ? 'pointer' : 'not-allowed'
                    }}
                >
                    Start Game
                </Button>
            </div>
        );
    }

    if (!gameStarted && !gameEnded && !showCharacterSelect && !showPressToStart) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '20px 16px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <div style={{ fontSize: '64px', marginBottom: '10px', transform: 'scaleX(-1)' }}>
                    {selectedCharacter?.emoji || playerEmojiRef.current}
                </div>
                <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '18px' }}>
                    🦘 Maveshi Jump
                </Title>
                <Text style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    display: 'block',
                    marginBottom: '10px'
                }}>
                    Click or press Space/Up to make your livestock fly! Avoid the obstacles.
                </Text>
                {highScore > 0 && (
                    <Text style={{ 
                        fontSize: '14px', 
                        color: '#fa8c16',
                        display: 'block',
                        marginBottom: '20px',
                        fontWeight: 600
                    }}>
                        🏆 High Score: {highScore}
                    </Text>
                )}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <Button
                        type="default"
                        size="large"
                        onClick={() => setShowCharacterSelect(true)}
                        style={{
                            height: '44px',
                            fontSize: '15px',
                            fontWeight: 600,
                            borderRadius: '10px',
                            padding: '0 24px'
                        }}
                    >
                        Change Character
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        icon={<TrophyOutlined />}
                        onClick={startGame}
                        style={{
                            height: '44px',
                            fontSize: '15px',
                            fontWeight: 600,
                            borderRadius: '10px',
                            padding: '0 24px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Start Game
                    </Button>
                </div>
            </div>
        );
    }

    // Press to Start screen
    if (showPressToStart && !gameStarted) {
        return (
            <div style={{ width: '100%', height: '500px', position: 'relative', overflow: 'hidden' }}>
                <div
                    ref={gameAreaRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(to bottom, #87CEEB 0%, #E0F6FF 50%, #98D8C8 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: '2px solid rgba(24, 144, 255, 0.2)',
                        cursor: 'pointer'
                    }}
                    onClick={handlePressToStart}
                >
                    {/* Ground */}
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '30px',
                        background: 'linear-gradient(to top, #8B4513 0%, #A0522D 100%)',
                        borderTop: '2px solid #654321'
                    }} />

                    {/* Player (idle animation) */}
                    <div
                        style={{
                            position: 'absolute',
                            left: '50px',
                            top: `${playerY}px`,
                            fontSize: '40px',
                            transform: 'translateY(-50%) scaleX(-1)',
                            filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))',
                            zIndex: 10,
                            animation: 'float 2s ease-in-out infinite'
                        }}
                    >
                        {selectedCharacter?.emoji || playerEmojiRef.current}
                    </div>

                    {/* Press to Start Overlay */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 30,
                        cursor: 'pointer'
                    }}>
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            padding: '30px 40px',
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                            textAlign: 'center',
                            border: '2px solid rgba(24, 144, 255, 0.3)'
                        }}>
                            <div style={{ fontSize: '64px', marginBottom: '15px', transform: 'scaleX(-1)' }}>
                                {selectedCharacter?.emoji || playerEmojiRef.current}
                            </div>
                            <Title level={3} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '24px' }}>
                                Press to Start
                            </Title>
                            <Text style={{ 
                                fontSize: '16px', 
                                color: '#666',
                                display: 'block',
                                marginBottom: '15px',
                                animation: 'pulse 1.5s ease-in-out infinite'
                            }}>
                                Click or Press Space/Up to Jump
                            </Text>
                            <div style={{
                                fontSize: '14px',
                                color: '#999',
                                marginTop: '10px'
                            }}>
                                <UpOutlined /> Space / Click / Arrow Up
                            </div>
                        </div>
                    </div>
                </div>

                <style>{`
                    @keyframes float {
                        0%, 100% {
                            transform: translateY(-50%) scaleX(-1) translateY(0);
                        }
                        50% {
                            transform: translateY(-50%) scaleX(-1) translateY(-10px);
                        }
                    }
                    @keyframes pulse {
                        0%, 100% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.6;
                        }
                    }
                `}</style>
            </div>
        );
    }

    if (gameEnded) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '30px 20px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
                <TrophyOutlined style={{ fontSize: '50px', color: '#ffd700', marginBottom: '15px' }} />
                <Title level={4} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '20px' }}>
                    Game Over!
                </Title>
                <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a', display: 'block', marginBottom: '10px' }}>
                    Score: {score}
                </Text>
                {score === highScore && score > 0 && (
                    <Text style={{ fontSize: '16px', color: '#fa8c16', display: 'block', marginBottom: '10px', fontWeight: 600 }}>
                        🎉 New High Score!
                    </Text>
                )}
                <Text style={{ fontSize: '16px', color: '#666', display: 'block', marginBottom: '20px' }}>
                    {getScoreMessage()}
                </Text>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <Button
                        type="default"
                        size="large"
                        onClick={() => {
                            setShowStartScreen(false);
                            setShowCharacterSelect(true);
                            setGameEnded(false);
                        }}
                        style={{
                            height: '44px',
                            fontSize: '15px',
                            fontWeight: 600,
                            borderRadius: '10px',
                            padding: '0 24px'
                        }}
                    >
                        Change Character
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        icon={<ReloadOutlined />}
                        onClick={startGame}
                        style={{
                            height: '44px',
                            fontSize: '15px',
                            fontWeight: 600,
                            borderRadius: '10px',
                            padding: '0 24px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Play Again
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '500px', position: 'relative', overflow: 'hidden' }}>
            {/* Game Area */}
            <div
                ref={gameAreaRef}
                style={{
                    width: '100%',
                    height: '100%',
                    background: collisionAnimation 
                        ? 'linear-gradient(to bottom, #ff6b6b 0%, #ff8787 50%, #ff9999 100%)'
                        : 'linear-gradient(to bottom, #87CEEB 0%, #E0F6FF 50%, #98D8C8 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    border: collisionAnimation 
                        ? '2px solid rgba(255, 0, 0, 0.5)'
                        : '2px solid rgba(24, 144, 255, 0.2)',
                    cursor: 'pointer',
                    transition: collisionAnimation ? 'none' : 'background 0.3s ease',
                    animation: collisionAnimation ? 'shake 0.3s ease-in-out' : 'none'
                }}
            >
                {/* Ground */}
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '30px',
                    background: 'linear-gradient(to top, #8B4513 0%, #A0522D 100%)',
                    borderTop: '2px solid #654321'
                }} />

                {/* Obstacles (Pipes) */}
                {obstacles.map(obstacle => (
                    <React.Fragment key={obstacle.id}>
                        {/* Top obstacle */}
                        <div
                            style={{
                                position: 'absolute',
                                left: `${obstacle.x}px`,
                                top: 0,
                                width: `${obstacle.width}px`,
                                height: `${obstacle.topHeight}px`,
                                background: 'linear-gradient(to right, #228B22 0%, #32CD32 50%, #228B22 100%)',
                                border: '2px solid #006400',
                                borderBottom: '4px solid #006400',
                                boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.3)'
                            }}
                        />
                        {/* Bottom obstacle */}
                        <div
                            style={{
                                position: 'absolute',
                                left: `${obstacle.x}px`,
                                bottom: 0,
                                width: `${obstacle.width}px`,
                                height: `${obstacle.bottomHeight}px`,
                                background: 'linear-gradient(to right, #228B22 0%, #32CD32 50%, #228B22 100%)',
                                border: '2px solid #006400',
                                borderTop: '4px solid #006400',
                                boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.3)'
                            }}
                        />
                    </React.Fragment>
                ))}

                {/* Player - hide on collision */}
                {!collisionAnimation && (
                    <div
                        style={{
                            position: 'absolute',
                            left: '50px',
                            top: `${playerY}px`,
                            fontSize: '40px',
                            transform: `translateY(-50%) scaleX(-1) rotate(${velocityY * 2}deg)`,
                            filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))',
                            transition: 'none',
                            zIndex: 10
                        }}
                    >
                        {playerEmojiRef.current}
                    </div>
                )}

                {/* Collectibles */}
                {collectibles.map(collectible => {
                    if (collectible.collected) return null;
                    const gameAreaWidth = gameAreaRef.current?.offsetWidth || 400;
                    const collectibleXPercent = (collectible.x / gameAreaWidth) * 100;
                    
                    return (
                        <div
                            key={collectible.id}
                            style={{
                                position: 'absolute',
                                left: `${collectibleXPercent}%`,
                                top: `${collectible.y}px`,
                                fontSize: '30px',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 11,
                                filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))',
                                animation: 'floatCollectible 2s ease-in-out infinite',
                                pointerEvents: 'none'
                            }}
                        >
                            {collectible.type === 'star' ? '⭐' : '🪙'}
                        </div>
                    );
                })}

                {/* Explosion Smoke Particles */}
                {explosionParticles.map(particle => (
                    <div
                        key={particle.id}
                        style={{
                            position: 'absolute',
                            left: `${particle.x}%`,
                            top: `${particle.y}px`,
                            width: `${particle.size}px`,
                            height: `${particle.size}px`,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, rgba(120, 120, 120, ${particle.opacity}) 0%, rgba(80, 80, 80, ${particle.opacity * 0.6}) 40%, rgba(40, 40, 40, ${particle.opacity * 0.3}) 70%, transparent 100%)`,
                            boxShadow: `0 0 ${particle.size}px rgba(100, 100, 100, ${particle.opacity * 0.5})`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: 12,
                            pointerEvents: 'none',
                            filter: 'blur(2px)'
                        }}
                    />
                ))}

                {/* Collision Flash Effect */}
                {collisionAnimation && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 0, 0, 0.2)',
                        zIndex: 11,
                        animation: 'flash 0.3s ease-in-out'
                    }} />
                )}

                {/* Score Display */}
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 20
                }}>
                    <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                        Score: {score}
                    </Text>
                    {collectedCount > 0 && (
                        <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#fa8c16' }}>
                            🪙 {collectedCount}
                        </Text>
                    )}
                </div>

                {/* Controls Hint */}
                <div style={{
                    position: 'absolute',
                    bottom: '40px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 20
                }}>
                    <Text style={{ fontSize: '12px', color: '#666' }}>
                        <UpOutlined /> Click or Press Space to Jump
                    </Text>
                </div>
            </div>

            <style>{`
                @keyframes shake {
                    0%, 100% {
                        transform: translateX(0);
                    }
                    10%, 30%, 50%, 70%, 90% {
                        transform: translateX(-5px);
                    }
                    20%, 40%, 60%, 80% {
                        transform: translateX(5px);
                    }
                }
                @keyframes collisionPulse {
                    0% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.3);
                    }
                    100% {
                        transform: scale(1.2);
                    }
                }
                @keyframes flash {
                    0% {
                        opacity: 0;
                    }
                    50% {
                        opacity: 0.5;
                    }
                    100% {
                        opacity: 0;
                    }
                }
                @keyframes floatCollectible {
                    0%, 100% {
                        transform: translate(-50%, -50%) translateY(0);
                    }
                    50% {
                        transform: translate(-50%, -50%) translateY(-8px);
                    }
                }
            `}</style>
        </div>
    );
};

export default MaveshiJump;
